import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { useEmbedParams } from 'src/hooks/useEmbedParams';
import { useParentMessageListener } from 'src/hooks/useParentMessageListener';
import { useSearchResponder } from 'src/hooks/useSearchResponder';
import { AuthProvider } from '../contexts/AuthContext';
import { BadgeColorProvider } from '../contexts/BadgeColorContext';
import { LayoutProvider } from '../contexts/LayoutContext';
import AppRoutes from '../routes/AppRoutes';
import { getDocumentTitle } from '../utils/documentTitleUtils';
import AuthWrapper from './auth/AuthWrapper';
import { AppErrorBoundary } from './common/AppErrorBoundary';
import TopNav from './common/TopNav';
import { GlobalAppLayout } from './layout/GlobalAppLayout';

/**
 * Main App component that sets up routing, authentication, and navigation
 *
 * This component:
 * - Configures AWS Amplify for authentication
 * - Sets up React Router for client-side routing
 * - Provides authentication context to all child components
 * - Provides layout context for dynamic AppLayout configuration
 * - Renders the top navigation and main content area with GlobalAppLayout
 *
 * Architecture:
 * - Single GlobalAppLayout instance wraps all routes
 * - Child components configure layout via LayoutContext
 * - Enables state persistence across navigation (e.g., AI chat drawer)
 */
/**
 * AppContent is rendered inside <BrowserRouter> so that hooks
 * like useNavigate() (used by useParentMessageListener) have
 * access to the Router context — enabling client-side navigation
 * without full page reloads when embedded in FinSuite.
 */
const AppContent: React.FC = () => {
  const { isEmbedded } = useEmbedParams();
  useParentMessageListener();
  useSearchResponder();

  useEffect(() => {
    document.title = getDocumentTitle();
  }, []);

  return (
    <AuthProvider>
      <BadgeColorProvider>
        <LayoutProvider>
          <AppErrorBoundary>
            <div>
              {!isEmbedded && (
                <div id="h">
                  <TopNav />
                </div>
              )}
              <div>
                <AuthWrapper>
                  <GlobalAppLayout>
                    <AppRoutes />
                  </GlobalAppLayout>
                </AuthWrapper>
              </div>
            </div>
          </AppErrorBoundary>
        </LayoutProvider>
      </BadgeColorProvider>
    </AuthProvider>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);

export default App;
